import { Search } from 'melp-design/icons';
import { Loader, Typography } from 'melp-design/components';
import { Effects, SystemColors } from 'melp-design/style';
import {
  Box,
  ClickAwayListener,
  IconButton,
  InputBase,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchResultEntity } from 'store/search';
import { useGlobalSearch } from 'state/GlobalSearch';
import { useMe } from 'state/Administrators';
import { ReactComponent as ChevronRight } from 'assets/icons/chevron-right.svg';
import { ReactComponent as Cross } from 'assets/icons/cross.svg';

export const GlobalSearch = () => {
  const { t } = useTranslation();
  const {
    hasBenefitPermissions,
    hasEmployeePermissions,
    hasAdministratorPermissions,
    hasNewsPermissions,
    hasGroupPermissions,
  } = useMe();
  const {
    search,
    results,
    loading,
    searchQuery,
    searchInputValue,
    clearSearch,
    handleSearchItemClick,
  } = useGlobalSearch();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
  }, [searchQuery]);

  const open = !dismissed && (!!results || (loading && !!searchQuery));

  const checkIfDisabled = useMemo(
    () => (entityType: SearchResultEntity) => {
      switch (entityType) {
        case 'admin':
          return !hasAdministratorPermissions;
        case 'benefitPlan':
          return !hasBenefitPermissions;
        case 'employee':
          return !hasEmployeePermissions;
        case 'employeeGroup':
          return !hasGroupPermissions;
        case 'news':
          return !hasNewsPermissions;
        default:
          return false;
      }
    },
    [
      hasAdministratorPermissions,
      hasBenefitPermissions,
      hasEmployeePermissions,
      hasGroupPermissions,
      hasNewsPermissions,
    ],
  );

  const resultsContent = (() => {
    if (loading) return <Loader minHeight={100} />;

    if (results && results.length === 0) {
      return (
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="p1">{t('errors.nothing_found')}</Typography>
        </Box>
      );
    }

    return (
      <MenuList sx={{ p: '6px' }}>
        {results?.map(({ entityId, description, benefitCategory, entity }) => (
          <MenuItem
            key={entityId}
            disabled={checkIfDisabled(entity)}
            onClick={() => handleSearchItemClick(entity, entityId)}
            sx={{
              borderRadius: '8px',
              py: 1,
              px: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              '&:hover': {
                backgroundColor: SystemColors.grey[98],
              },
            }}
          >
            <Box>
              <Typography component="span" variant="p1">
                {description}
              </Typography>
              <Typography
                component="span"
                variant="p2"
                color="textSecondary"
                sx={{ pl: '5px' }}
              >
                {benefitCategory ? `- ${t(`menu.${benefitCategory}`)} ` : ''}-{' '}
                {t(`entity_type.${entity}`)}
              </Typography>
            </Box>
            <ChevronRight />
          </MenuItem>
        ))}
      </MenuList>
    );
  })();

  return (
    <ClickAwayListener onClickAway={() => setDismissed(true)}>
      <Box ref={anchorRef} sx={{ position: 'relative' }}>
        <InputBase
          value={searchInputValue}
          placeholder={`${t('common.search')}...`}
          onChange={({ target }) => search(target.value)}
          startAdornment={
            <Box
              sx={{
                display: 'flex',
                color: SystemColors.grey[45],
                mr: 1,
                flexShrink: 0,
              }}
            >
              <Search />
            </Box>
          }
          endAdornment={
            searchInputValue ? (
              <IconButton
                size="small"
                onClick={clearSearch}
                aria-label={t('common.close')}
                sx={{ mr: -0.5 }}
              >
                <Cross />
              </IconButton>
            ) : null
          }
          inputProps={{
            'aria-label': t('common.search'),
            autoComplete: 'off',
            'aria-autocomplete': 'none',
          }}
          sx={{
            width: 240,
            height: 36,
            px: 1.5,
            borderRadius: '999px',
            backgroundColor: SystemColors.grey[96],
            fontSize: 14,
            color: SystemColors.black,
            transition: 'background-color 150ms, box-shadow 150ms',
            '&:hover': {
              backgroundColor: SystemColors.grey[98],
            },
            '&.Mui-focused': {
              backgroundColor: SystemColors.white,
              boxShadow: `0 0 0 1px ${SystemColors.grey[90]}`,
            },
          }}
        />
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-end"
          disablePortal
          style={{ zIndex: 1100 }}
        >
          <Paper
            sx={{
              mt: '8px',
              width: 360,
              maxHeight: 360,
              overflow: 'auto',
              borderRadius: '8px',
              boxShadow: Effects.smallElementsShadow,
            }}
          >
            {resultsContent}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};
